<template>
  <v-app
    :theme="uiStore.vuetifyThemeName"
    :class="[uiStore.themeClass, uiStore.accentClass]"
    :style="uiStore.accentCssVars"
  >
    <div class="bm-bg" :style="{ backgroundImage: `url(${bgImage})` }" />

    <FloatingTopNav v-if="authStore.isAuthenticated" />
    <FloatingSideNav v-if="authStore.isAuthenticated" />

    <v-main
      :class="[
        authStore.isAuthenticated ? 'bm-main--authed' : '',
        authStore.isAuthenticated && !uiStore.sideNavOpen ? 'bm-main--nav-collapsed' : '',
      ]"
    >
      <div
        class="bm-content-wrap transition-all duration-300"
        :class="authStore.isAuthenticated ? 'bm-content-wrap--authed pt-20 pb-6 px-3 md:px-4' : 'bm-content-wrap--public'"
      >
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </div>
    </v-main>

    <ConfirmDialog />
  </v-app>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useAuthStore } from './store/auth';
import { useUiStore } from './store/ui';
import { useConfigStore } from './store/config';
import { useNotificationStore } from './store/notifications';
import { lightBackground, darkBackground } from './assets/backgrounds';
import FloatingTopNav from './components/nav/FloatingTopNav.vue';
import FloatingSideNav from './components/nav/FloatingSideNav.vue';
import ConfirmDialog from './components/dialogs/ConfirmDialog.vue';

const authStore = useAuthStore();
const uiStore = useUiStore();
const configStore = useConfigStore();
const notificationStore = useNotificationStore();

const bgImage = computed(() => (uiStore.theme === 'dark' ? darkBackground : lightBackground));

async function bootstrap() {
  // The router guard already awaits fetchMe() before the first navigation
  // resolves; skip the redundant call here so boot doesn't double-fire
  // /auth/me (and the appearance-sync PATCH it can trigger) on every load.
  if (!authStore.hydrated) {
    await authStore.fetchMe();
  }
  await configStore.fetch();
  if (authStore.isAuthenticated) {
    await notificationStore.fetch();
    notificationStore.listen();
    if (authStore.user?.theme || authStore.user?.themeAccent) {
      uiStore.setAppearance({
        theme: authStore.user.theme,
        themeAccent: authStore.user.themeAccent,
      }, {
        touch: false,
        userId: authStore.user._id || authStore.user.id,
      });
    }
  }
}

onMounted(bootstrap);

watch(() => authStore.isAuthenticated, (isAuthed) => {
  if (isAuthed) {
    notificationStore.fetch();
    notificationStore.listen();
  }
});
</script>

<style>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.bm-content-wrap {
  position: relative;
  z-index: 1;
}

.bm-content-wrap--public {
  min-height: 100vh;
  padding: 0;
}

@media (min-width: 768px) {
  .bm-main--authed .bm-content-wrap,
  .bm-content-wrap--authed {
    padding-left: 17rem !important;
    padding-right: 1rem !important;
  }

  .bm-main--nav-collapsed .bm-content-wrap {
    padding-left: 6.75rem !important;
  }
}

@media (min-width: 640px) and (max-width: 767px) {
  .bm-main--authed .bm-content-wrap,
  .bm-content-wrap--authed {
    padding-left: 6rem !important;
  }
}
</style>
